import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, change, formValueSelector } from 'redux-form';
import { withRouter } from 'react-router';
import v from 'validate.js';
import styles from './style.module.css';
import * as Routes from '../../../../../constants/routes';
import { trackingEvent } from '../../../../../helpers/tracking';
import { onSubmit } from '../../../../../common/components/submit-button';
import logger from '../../../../../helpers/logger';

export const formName = Routes.STEPSIX;

const tracking = {
    eventAction: 'Mortgage Calculator - Step 6',
    eventLabel: 'Your Mortgage Term'
};

const validate = values => v(values, {
    paymentFrequency: {
        presence: {
            allowEmpty: false,
            message: 'is required'
        }
    }
});

const paymentFrequencyOptions = {
    'Weekly': {
        title: 'Weekly Payment',
        content: 'Your monthly mortgage payment is multiplied by 12 months and divided by 52 weeks of the year, resulting in 52 payments per year.',
        frequency: 52
    },
    'Weekly (Accelerated)': {
        title: 'Accelerated Weekly Payment',
        content: 'You still make 52 mortgage payments per year, however, the payment amount is slightly higher than a weekly payment. With this option, your monthly mortgage amount is divided by four and paid every week.',
        frequency: 52
    },
    'Bi-Weekly': {
        title: 'Bi-Weekly Payment',
        content: 'Make 26 payments a year \u2014 your monthly mortgage payment is multiplied by 12 months and divided by 26 pay periods in a year.',
        frequency: 26
    },
    'Bi-Weekly (Accelerated)': {
        title: 'Accelerated Bi-Weekly Payment',
        content: 'You still make 26 mortgage payments per year, however, the payment amount is slightly higher than a regular bi-weekly payment. Your monthly mortgage payment is divided by two and the amount is paid every two weeks.',
        frequency: 26
    },
    'Semi-Monthly': {
        title: 'Semi-Monthly Payment',
        content: 'Make payments twice a month, for example, on the 1st and 15th of each month. Semi-monthly payments are different than bi-weekly payments because if you pay on two dates per month, you make 24 annual payments. Bi-weekly payments result in 26 annual payments.',
        frequency: 24
    },
    'Monthly': {
        title: 'Monthly Payment',
        content: 'Your monthly mortgage payment is multiplied by 12 months and divided by 52 weeks of the year, resulting in 52 payments per year.',
        frequency: 12
    }
};

// No default selection per design

const PaymentCard = ({ option, optionKey, selected, expanded, onSelect, onToggle }) => (
    <div className={selected ? styles.cardActive : styles.card} role='radio' aria-checked={selected}>
        <div className={styles.cardHeader}>
            <button
                type='button'
                className={styles.radioButton}
                onClick={() => onSelect(optionKey)}
                aria-label={`Select ${option.title}`}
            >
                <span className={selected ? styles.radioChecked : styles.radio} />
                <span className={styles.cardTitle}>{option.title}</span>
            </button>
            <button
                type='button'
                className={styles.chevronButton}
                onClick={() => onToggle(optionKey)}
                aria-expanded={expanded}
                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${option.title} details`}
            >
                <span className={expanded ? styles.chevronUp : styles.chevronDown} />
            </button>
        </div>
        {expanded && (
            <p className={styles.cardContent}>{option.content}</p>
        )}
    </div>
);

class StepSixForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            expandedCard: null
        };
    }

    handleToggle(key) {
        logger.logUserAction('payment_frequency_details_toggled', 'MortgageCalculator-Step6', {
            step: 6,
            paymentOption: key,
            action: this.state.expandedCard === key ? 'collapsed' : 'expanded'
        });

        this.setState(prevState => ({
            expandedCard: prevState.expandedCard === key ? null : key
        }));
    }

    componentDidUpdate(prevProps) {
        // Log validation errors
        if (this.props.invalid && this.props.submitFailed && this.props.syncErrors) {
            logger.warn('Form validation failed in Step 6', {
                step: 6,
                formName: formName,
                errors: this.props.syncErrors,
                values: this.props.values
            });
        }

        // Log payment frequency selection changes
        if (prevProps.selectedFrequency !== this.props.selectedFrequency) {
            logger.logUserAction('payment_frequency_selected', 'MortgageCalculator-Step6', {
                step: 6,
                previousFrequency: prevProps.selectedFrequency,
                selectedFrequency: this.props.selectedFrequency,
                frequencyDetails: this.props.selectedFrequency ? paymentFrequencyOptions[this.props.selectedFrequency] : null
            });
        }

        const { changeFieldValue, selectedFrequency } = this.props;
        if (selectedFrequency) {
            changeFieldValue('paymentFrequencyNum', paymentFrequencyOptions[selectedFrequency].frequency);
        }
    }

    render() {
        const { selectedFrequency } = this.props;
        const { expandedCard } = this.state;

        return (
            <form className={styles.form}>
                <label className={styles.label}>{"What's your preferred payment frequency?"}</label>
                <Field name='paymentFrequency' component={({ meta: { error, submitFailed } }) => (
                    submitFailed && error ? <span className={styles.errorMessage} role='alert'>{error[0]}</span> : null
                )} />
                <Field name='paymentFrequencyNum' component='input' type='hidden' />
                <div className={styles.cardContainer} role='radiogroup' aria-label='Payment frequency options'>
                    {Object.keys(paymentFrequencyOptions).map(key => (
                        <PaymentCard
                            key={key}
                            optionKey={key}
                            option={paymentFrequencyOptions[key]}
                            selected={selectedFrequency === key}
                            expanded={expandedCard === key}
                            onSelect={(val) => {
                                logger.logUserAction('payment_frequency_card_clicked', 'MortgageCalculator-Step6', {
                                    step: 6,
                                    selectedOption: val,
                                    optionDetails: paymentFrequencyOptions[val]
                                });
                                this.props.change('paymentFrequency', val);
                            }}
                            onToggle={(val) => this.handleToggle(val)}
                        />
                    ))}
                </div>
            </form>
        );
    }
}

const selector = formValueSelector(formName);

const mapStateToProps = (state) => ({
    selectedFrequency: selector(state, 'paymentFrequency')
});

const mapDispatchToProps = (dispatch) => ({
    changeFieldValue: (field, value) => dispatch(change(formName, field, value))
});

// Enhanced onSubmit with logging
const enhancedOnSubmit = async (values, dispatch, props) => {
    try {
        const result = await onSubmit(values, dispatch, props);
        logger.logFormSubmission('MortgageCalculator-Step6', values, true);
        return result;
    } catch (error) {
        logger.error('Step 6 form submission failed', {
            step: 6,
            formName: formName,
            values,
            error: error.message || error
        });
        throw error;
    }
};

StepSixForm = reduxForm({
    form: formName,
    destroyOnUnmount: false,
    validate,
    onSubmit: enhancedOnSubmit,
    initialValues: {},
    redirectPath: `/mortgage${Routes.STEPSEVEN}`,
    tracking: () => trackingEvent(tracking)
})(StepSixForm);

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(StepSixForm));
